import type { VariantProps } from 'class-variance-authority';

import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import * as React from 'react';
import { Icon } from '@/components/entities/Sprite';
import { cn } from '@/utils/cn';

type Size = 'sm' | 'md' | 'lg';

const buttonVariants = cva(
  `flex items-center justify-center gap-2 whitespace-nowrap
  text-sm font-medium transition-all cursor-pointer disabled:pointer-events-none active:outline-none 
        active:ring-1 
        active:ring-white 
        active:ring-offset-1 
        active:ring-offset-transparent active:shadow-[0_0_0_4px_#BD4DF9]  [&_svg]:pointer-events-none [&_svg:not([class*="size-"])]:size-4 shrink-0 [&_svg]:shrink-0 
   `,
  {
    variants: {
      variant: {
        primary: `
        relative
        rounded-[6.25rem]
        text-white shadow-xs
        transition-all duration-300
        overflow-hidden
        z-[1]
        before:absolute before:bg-main-back-gradient before:inset-0 before:content-[''] before:z-[-1]
        after:absolute after:bg-primary-1 after:inset-0 after:opacity-0 after:transition-opacity after:duration-300 after:content-[''] after:z-[-1]
        
        hover:after:opacity-100
        [&>*]:relative [&>*]:z-[1]

        focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#FF7B004D] focus-visible:ring-offset-1 focus-visible:ring-offset-transparent focus-visible:shadow-[0_0_0_4px_#FF7B004D]
        disabled:opacity-40 bg-none
      `,
        secondary: `bg-main-800 text-white outline outline-1 outline-transparent hover:outline-c600 -outline-offset-1 hover:bg-primary-4-dark disabled:bg-main-800/60 disabled:text-violet-c500 focus-visible:outline-none focus-visible:ring-offset-transparent focus-visible:shadow-[0_0_0_2px_#FFFFFF1A] focus:bg-main-800`,
        stroked: `
          relative rounded-[6.25rem]
          overflow-hidden
          isolate
          outline
          outline-primary-4-dark
          text-white
          isolate
          before:absolute before:bg-header-gradient before:inset-0 before:content-[''] before:z-[-1]
          after:absolute after:bg-c900 after:inset-0 after:opacity-0 after:transition-opacity after:duration-300 after:content-[''] after:z-[-1]
          hover:after:opacity-100
          hover:outline hover:outline-c600
          [&>*]:relative [&>*]:z-[1]
          disabled:bg-main-800 disabled:opacity-40 disabled:outline disabled:outline-main-700
          focus-visible:outline-none focus-visible:ring-offset-transparent focus-visible:shadow-[0_0_0_2px_#4D3069] focus:bg-c900
        `,
        tertiary:
          'bg-transparent text-white hover:text-white/70 disabled:text-white/30 active:shadow-none active:ring-0 active:ring-offset-0',
      },
      size: {
        sm: 'rounded-[6.25rem] gap-1.5',
        md: 'rounded-[6.25rem] has-[>svg]:px-3',
        lg: 'rounded-[6.25rem] has-[>svg]:px-4',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'sm',
    },
  },
);

function Button({
  className,
  spanClassName,
  variant = 'primary',
  size = 'md',
  asChild = false,
  iconBefore,
  iconAfter,
  children,
  isLoading = false,
  disabled,
  ...props
}: React.ComponentProps<'button'>
  & VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    iconBefore?: React.ReactNode;
    iconAfter?: React.ReactNode;
    size?: Size;
    isLoading?: boolean;
    spanClassName?: string;
  }) {
  const Comp = asChild ? Slot : 'button';

  function getGapClass(size: Size): string {
    switch (size) {
      case 'sm':
        return 'gap-1';
      case 'md':
        return 'gap-2';
      case 'lg':
        return 'gap-3';
      default:
        return '';
    }
  }

  if (asChild) {
    return (
      <Comp
        data-slot="button"
        className={cn(
          'relative',
          buttonVariants({ variant, size }),
          className,
          getGapClass(size),
        )}
        disabled={disabled}
        {...props}
      >
        {children}
      </Comp>
    );
  }

  return (
    <Comp
      data-slot="button"
      className={cn(
        'relative',
        buttonVariants({ variant, size }),
        className,
        getGapClass(size),
      )}
      disabled={disabled}
      {...props}
    >

      <span className={cn('flex items-center gap-2 justify-center', spanClassName)}>
        {iconBefore && <span>{iconBefore}</span>}
        {children}
        {isLoading && <Icon name="loading" className="size-5 text-light animate-spin" />}
        {iconAfter && <span>{iconAfter}</span>}
      </span>
    </Comp>
  );
}

export { Button, buttonVariants };
